import { memo } from "react";
import cx from "classnames";
import { useShallow } from "zustand/react/shallow";

import Box from "ds/components/Box";

import useMainNavigationStore from "./stores/useMainNavigationStore";
import { selectNavigationShowsExpandedContent } from "./selectors/navigationState";
import { useMainNavigationInitialization } from "./hooks/useInitialization";
import useMainNavigationScrollDetection from "./hooks/useScrollDetection";
import useMainNavigationSidebarPreview from "./hooks/useSidebarPreview";
import MainNavigationHeader from "./components/layout/Header";
import MainNavigationPrimaryItems from "./components/layout/PrimaryItems";
import MainNavigationSecondaryItems from "./components/layout/SecondaryItems";
import MainNavigationGradient from "./components/layout/Gradient";
import styles from "./styles.module.css";

const MainNavigation = () => {
  const { showsExpandedContent, isSidebarInPreview, mobileFullScreen } = useMainNavigationStore(
    useShallow((state) => ({
      showsExpandedContent: selectNavigationShowsExpandedContent(state),
      isSidebarInPreview: state.isSidebarInPreview,
      mobileFullScreen: state.mobileFullScreen,
    }))
  );

  const { scrollContainerRef, canScrollUp, canScrollDown } = useMainNavigationScrollDetection();
  const sidebarPreviewHandlers = useMainNavigationSidebarPreview();

  useMainNavigationInitialization();

  return (
    <Box
      id="main-navigation"
      className={cx(styles.mainNavigation, {
        [styles.expanded]: showsExpandedContent,
        [styles.preview]: isSidebarInPreview,
        [styles.mobileFullScreen]: mobileFullScreen,
      })}
      direction="column"
      tabIndex={-1}
      {...sidebarPreviewHandlers}
    >
      <MainNavigationGradient />

      <MainNavigationHeader />

      <Box
        className={cx(styles.navigationBodyWrapper, {
          [styles.canScrollUp]: canScrollUp,
          [styles.canScrollDown]: canScrollDown,
        })}
        grow="1"
        relative
        zeroMinHeight
        fullWidth
      >
        <Box
          ref={scrollContainerRef}
          direction="column"
          className={styles.navigationBody}
          scrollable
          fullWidth
        >
          <MainNavigationPrimaryItems />
        </Box>
      </Box>

      <Box direction="column" shrink="0">
        <MainNavigationSecondaryItems />
      </Box>
    </Box>
  );
};

export default memo(MainNavigation);
